import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Calendar, DollarSign, TrendingUp, Bus, LogOut, MapPin } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import type { Lead, Property } from "@shared/schema";
import PropertyDetailModal from "@/components/property-detail-modal";

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  
  // Check authentication on component mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isAuthenticated = localStorage.getItem('adminAuthenticated');
    const loginTime = localStorage.getItem('adminLoginTime');
    
    // Check if user is authenticated and session is valid (24 hours)
    if (!isAuthenticated || (loginTime && Date.now() - parseInt(loginTime) > 24 * 60 * 60 * 1000)) {
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminLoginTime');
      setLocation('/admin/login');
      return;
    }
  }, [setLocation]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminLoginTime');
    }
    
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out",
    });
    
    setLocation('/');
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["/api/dashboard/stats"],
  });

  const { data: leads, isLoading: leadsLoading } = useQuery<Lead[]>({
    queryKey: ["/api/leads"],
  });

  // Fetch properties for all leads
  const propertyIds = leads?.map(lead => lead.propertyId).filter(Boolean) || [];
  const { data: properties } = useQuery<Property[]>({
    queryKey: ["/api/properties"],
    enabled: propertyIds.length > 0,
  });

  // Create a map of propertyId -> Property for quick lookup
  const propertyMap = new Map<string, Property>();
  properties?.forEach(prop => {
    if (prop.id) {
      propertyMap.set(prop.id, prop);
    }
  });

  const handleLeadClick = async (propertyId: string) => {
    // Check if we already have the property in our map
    let property = propertyMap.get(propertyId);
    
    // If not, fetch it
    if (!property) {
      try {
        const response = await fetch(`/api/properties/${propertyId}/details`);
        if (response.ok) {
          property = await response.json();
          propertyMap.set(propertyId, property);
        }
      } catch (error) {
        console.error('Failed to fetch property:', error);
        toast({
          title: "Error",
          description: "Failed to load property details",
          variant: "destructive",
        });
        return;
      }
    }
    
    if (property) {
      setSelectedProperty(property);
      setIsPropertyModalOpen(true);
    }
  };

  const formatPrice = (price: number | string | null | undefined): string => {
    if (!price) return 'Price on request';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'Price on request';
    return `$${numPrice.toLocaleString()}`;
  };

  if (statsLoading || leadsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      new: "bg-mint-green/20 text-mint-green border-mint-green/30",
      contacted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      scheduled: "bg-mint-green/20 text-mint-green border-mint-green/30",
      completed: "bg-blue-100 text-blue-800 border-blue-200",
      closed: "bg-gray-100 text-gray-800 border-gray-200",
    };
    
    const statusLabels = {
      new: "New",
      contacted: "Follow Up",
      scheduled: "Scheduled",
      completed: "Completed",
      closed: "Closed",
    };

    return (
      <Badge 
        variant="outline" 
        className={statusStyles[status as keyof typeof statusStyles] || statusStyles.new}
      >
        {statusLabels[status as keyof typeof statusLabels] || status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-deep-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-luxury-gold rounded-full flex items-center justify-center">
                <Bus className="w-6 h-6 text-deep-navy" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Agent Dashboard</h1>
                <p className="text-blue-100">Welcome back, Quentin</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-white hover:text-luxury-gold hover:bg-white/10"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-luxury-gold">
                ${typeof stats?.potentialCommission === 'number' 
                  ? stats.potentialCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : parseFloat(stats?.potentialCommission || "0").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-blue-100 text-sm">Potential Commission This Month</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to site */}
        <div className="mb-6">
          <Link href="/">
            <Button variant="ghost" className="text-deep-navy hover:text-ocean-blue">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Website
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-mint-green/20 bg-mint-green/5">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-mint-green mr-2" />
                <div className="text-3xl font-bold text-mint-green">
                  {stats?.newLeads || 0}
                </div>
              </div>
              <div className="text-sm text-gray-600">New Leads</div>
            </CardContent>
          </Card>

          <Card className="border-luxury-gold/20 bg-luxury-gold/5">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Calendar className="w-6 h-6 text-yellow-600 mr-2" />
                <div className="text-3xl font-bold text-yellow-600">
                  {stats?.scheduledTours || 0}
                </div>
              </div>
              <div className="text-sm text-gray-600">Scheduled Tours</div>
            </CardContent>
          </Card>

          <Card className="border-ocean-blue/20 bg-ocean-blue/5">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-ocean-blue mr-2" />
                <div className="text-3xl font-bold text-ocean-blue">
                  {stats?.activeContacts || 0}
                </div>
              </div>
              <div className="text-sm text-gray-600">Active Contacts</div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-6 h-6 text-purple-600 mr-2" />
                <div className="text-3xl font-bold text-purple-600">
                  {stats?.dealsInProgress || 0}
                </div>
              </div>
              <div className="text-sm text-gray-600">Deals in Progress</div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-playfair text-2xl text-deep-navy">Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property Interest
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Potential Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads?.map((lead) => {
                    const property = propertyMap.get(lead.propertyId);
                    return (
                      <tr 
                        key={lead.id} 
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleLeadClick(lead.propertyId)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-deep-navy">
                              {lead.firstName} {lead.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{lead.email}</div>
                            <div className="text-xs text-gray-400">{lead.phone}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {property ? (
                            <div>
                              <div className="font-semibold text-deep-navy mb-1">
                                {formatPrice(property.price)}
                              </div>
                              <div className="flex items-start text-gray-600">
                                <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span className="text-xs">
                                  {property.address || `${property.city}, ${property.state}`}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-400 italic">Loading property...</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          {lead.leadType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(lead.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-luxury-gold">
                          ${parseFloat(lead.potentialCommission || "0").toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(lead.createdAt!).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {!leads?.length && (
                <div className="text-center py-8 text-gray-500">
                  No leads found. Start promoting your properties to generate leads!
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        isOpen={isPropertyModalOpen}
        onClose={() => {
          setIsPropertyModalOpen(false);
          setSelectedProperty(null);
        }}
        property={selectedProperty}
      />
    </div>
  );
}
